import { z } from 'zod';
import { EmailSchema, IDSchema, IsoTimestampSchema, SchemaVersionSchema } from './shared.js';
/**
 * AffectedEntity controlled vocabulary covers all catalog entities + the
 * operational tables whose rows can be the subject of an audit event.
 * Updated when the catalog grows OR when an operational table grows a
 * cron / management surface that audits its own rows (e.g.,
 * `webhook_idempotency.purge_run` audits webhook_idempotency rows as
 * the affected subject — same pattern as token-purge does for tokens).
 */
export const AffectedEntitySchema = z.enum([
    'account',
    'contact',
    'deal',
    'quote',
    'submission',
    'approval',
    'meeting',
    'activity',
    'task',
    'commission',
    'commission_statement',
    'contractor',
    'token',
    'document',
    // RIG-393 — admin static-knowledge documents (RAG corpus); subject of
    // upload/delete audit events on the admin knowledge surface.
    'knowledge_document',
    'invoice',
    'pricing_config',
    'alerting_config',
    'notification_subscription',
    'notification',
    'email_template',
    'user_profile',
    'system_config',
    'webhook_secrets',
    'push_subscriptions',
    'user_role_assignment',
    // T-TEAM-001-FOUNDATION — Team scoping foundation entities.
    'team',
    'user_team_membership',
    // Operational tables whose rows are subjects of audit events:
    'webhook_idempotency',
    'bulk_import_draft',
    'intake_draft',
    // T9-011 — outlook mailbox subscription rows.
    'outlook_subscription',
    // T9-016 — search-query audit rows reference the staff actor; surface
    // class is 'search' for grouping.
    'search',
]);
/**
 * AuditEntry — the canonical audit record. Append-only: no version, no updatedAt,
 * no deletedAt. Range-partitioned by `created_at` month at the database level
 * (see infra/post-deploy/apply-audit-partitioning.sql).
 */
export const AuditEntrySchema = z.object({
    id: IDSchema,
    type: z.literal('audit_entry'),
    schemaVersion: SchemaVersionSchema,
    createdAt: IsoTimestampSchema,
    eventType: z.string(),
    actorEmail: EmailSchema,
    actorRole: z.string().optional(),
    actorIp: z.string().optional(),
    affectedEntity: AffectedEntitySchema,
    affectedId: z.string(),
    affectedAccountId: z.string().optional(),
    priorState: z.unknown().optional(),
    newState: z.unknown().optional(),
    comment: z.string().optional(),
    metadata: z.record(z.unknown()).default({}),
});
/**
 * Input shape for `auditWrite()`. The helper generates `id`, `type`, `schemaVersion`,
 * and `createdAt` server-side.
 */
export const AuditWriteInputSchema = AuditEntrySchema.omit({
    id: true,
    type: true,
    schemaVersion: true,
    createdAt: true,
});
//# sourceMappingURL=audit.js.map